<template>
  <div :class="$style.container">
    <div :class="$style.fieldContainer">
      <div :class="$style.row">
        <b-field :class="$style.field">
          <b-dropdown
            v-model="selectedGripDomainCategory"
            aria-role="list"
            :class="$style.dropdownContainer"
          >
            <button
              class="button"
              :class="$style.dropdownTrigger"
              slot="trigger"
            >
              <span>
                {{ selectedGripDomainCategory }}
              </span>
              <b-icon icon="menu-down"></b-icon>
            </button>
            <b-dropdown-item
              aria-role="listitem"
              v-for="(gripDomainCategory, index) in gripDomainCategoryOptions"
              @click="handleSelectCategory(gripDomainCategory)"
              :value="gripDomainCategory"
              :key="index"
            >
              <span v-if="selectedGripDomainCategory !== gripDomainCategory">{{
                gripDomainCategory
              }}</span>
            </b-dropdown-item>
            <a :class="$style.createNewCategoryButton" @click="handleCreate()">
              <span>Create New Domain/Category</span>
            </a>
          </b-dropdown>
        </b-field>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Action, State } from 'vuex-class';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import DomainCategoryModal from './DomainCategoryModal.vue';
import { GripDomainCategory } from '../../../store/types/grip.types';
import { isTruthy } from '../../../jbi-shared/util/watcher.vue-decorator';
import { ApiState } from '../../../store/types/general.types';

@Component({
  components: { DomainCategoryModal },
})
export default class DomainCategoryDropdown extends Vue {
  public gripDomainCategoryOptions: string[] = [];
  public selectedGripDomainCategory: string | null = 'Select Domain/Category';

  @Prop() public projectId!: number;
  @Prop() public modalTitle!: string;
  @Prop() public isNew!: boolean;
  @Prop() public gripDomainCategoryDetails!: GripDomainCategory;
  @Prop() public gripDomainCategories!: GripDomainCategory[];

  @Action('projects/getGripDomainCategories')
  public getGripDomainCategories!: (projectId: number) => void;

  @State((state) => state.projects.apiState.getGripDomainCategories)
  public getGripDomainCategoriesState!: ApiState;

  public getGripDomainCategoryOptions(): void {
    this.gripDomainCategoryOptions = this.gripDomainCategories
      .filter(
        (data: GripDomainCategory) =>
          data.name !== this.selectedGripDomainCategory,
      )
      .map((data: GripDomainCategory) => data.name);

    if (
      this.selectedGripDomainCategory !==
      `No ${this.modalTitle} Domain/Category`
    ) {
      this.gripDomainCategoryOptions.push(
        `No ${this.modalTitle} Domain/Category`,
      );
    }
  }

  public handleSelectCategory(selectedOption: string): void {
    this.getGripDomainCategoryOptions();
    const selectedGripDomainCategoryOption:
      | GripDomainCategory
      | undefined = this.gripDomainCategories.find(
      (gripDomainCategory) => gripDomainCategory.name === selectedOption,
    );
    this.$emit(
      'selectedGripDomainCategory',
      selectedGripDomainCategoryOption
        ? selectedGripDomainCategoryOption
        : null,
    );
  }

  public handleCreate(): void {
    this.$buefy.modal.open({
      parent: this,
      props: {
        projectId: this.projectId,
        title: 'Add',
      },
      events: {
        close: (isError: boolean) => {
          if (isError) {
            this.$emit('close');
          }
        },
        submit: (newCategory: GripDomainCategory) => {
          this.selectedGripDomainCategory = newCategory.name;
          this.$emit('selectedGripDomainCategory', newCategory);
        },
      },
      component: DomainCategoryModal,
      hasModalCard: true,
    });
  }

  public syncSelectedFromDetails(): void {
    if (this.isNew) {
      this.gripDomainCategoryOptions = this.gripDomainCategories.map(
        (gripDomainCategory) => gripDomainCategory.name,
      );
      return;
    }

    this.selectedGripDomainCategory = this.gripDomainCategoryDetails
      ? this.gripDomainCategoryDetails.name
      : `No ${this.modalTitle} Domain/Category`;
    this.getGripDomainCategoryOptions();
  }

  public mounted(): void {
    this.syncSelectedFromDetails();
  }

  @Watch('gripDomainCategoryDetails', { immediate: true })
  public onGripDomainCategoryDetailsChange(): void {
    if (!this.isNew) {
      this.syncSelectedFromDetails();
    }
  }

  @Watch('getGripDomainCategoriesState.success')
  @isTruthy
  public watchGripDomainCategorySuccess(): void {
    this.getGripDomainCategoryOptions();
  }
}
</script>

<style lang="scss" module>
.container {
  .fieldContainer {
    padding-top: 0.5rem;
  }

  .row {
    display: flex;
    flex: 1;
    align-items: center;
  }

  .field {
    width: 100%;
    align-items: center;
    margin-bottom: 1rem;
  }

  .deleteButtonContainer {
    min-width: 56px;

    .deleteButton {
      padding: 0 1rem;
    }
  }

  .dropdownContainer {
    :global(.dropdown-menu) {
      padding-top: 0;
      z-index: 40;

      :global(.dropdown-content) {
        padding-top: 0;
        padding-bottom: 0;

        a {
          border-bottom: 0.5px solid $grey-border;

          &:last-child {
            padding: 0.5rem;
            border-bottom: none;
          }

          p {
            font-weight: 400;
            color: $grey;
          }
        }
        a.is-active {
          border-bottom: 1px solid #dbdbdb;
          background-color: #eff7ff;
          padding: 10px 12px;
          display: contents;
        }
        a.dropdown-item.is-active,
        .dropdown .dropdown-menu .has-link a.is-active,
        button.dropdown-item.is-active {
          background-color: #00205b;
          color: #fff;
          display: contents;
        }
      }
    }

    .dropdownTrigger {
      font-weight: normal !important;
      width: 100%;
      justify-content: space-between;
      padding: 8px 16px;
    }

    .createNewCategoryButton {
      display: block;
      width: 100%;
      color: $secondary;
      font-weight: 600;

      &:hover {
        background-color: whitesmoke;
      }
    }
  }

  :global(.b-checkbox.checkbox .control-label) {
    font-weight: normal;
  }
}
</style>
